import { useTranslation } from "react-i18next";
import type { DataMutate, OptionType } from "../../../../types/types";
import type {
  AttributeSchema,
  AttributeValue,
  CollectionSchema,
  ImageAttributeValue,
  IproductReferenceCollectionItem,
  PIMProduct,
} from "../../../../types/types.PIM";
import {
  toTitleCase,
  useStoreState,
  useUpdateProductStatus,
} from "../../../../util/util";
import {
  addRow,
  deleteRow,
  editCollectionRow,
  GENERATED_UUID,
} from "../../../admin/SellerAdmin/PIM/components/ProductCollectionSchemaTable/ProductCollectionSchema.util";
import { ProductCollectionSchemaTable } from "../../../admin/SellerAdmin/PIM/components/ProductCollectionSchemaTable/ProductCollectionSchemaTable";
import type {
  FormValue,
  OnSubmitValue,
} from "../../../admin/SellerAdmin/PIM/components/ProductCollectionSchemaTable/type";
import { CustomSKUConfig } from "./CustomSKUConfig";
import type { ChipType } from "../../../../components/Chips/Chips";
import { isExistingAttributeFormValue } from "../ProductOverview/product_overview.utils";
import { useMemo } from "react";
import moment from "moment";

export const CollectionComponent = ({
  collection,
  product,
  fetchProductData,
  isPortfolio = false,
  replaceProductId,
}: {
  collection: CollectionSchema;
  product: PIMProduct;
  fetchProductData: DataMutate<PIMProduct>;
  replaceProductId: (id: string) => void;
  isPortfolio?: boolean;
}) => {
  const { tenant_id } = useStoreState();
  const { t } = useTranslation();
  const updateProductStatus = useUpdateProductStatus({ product });

  const attrMap = useMemo(
    () => new Map(collection.columns.map((attr) => [attr.id, attr])),
    [collection.columns]
  );

  const getUpdatedProductRowId = (
    rowId: string,
    updatedProduct: PIMProduct
  ) => {
    const updatedProductCollection =
      updatedProduct.product_schema.collections.find(
        ({ id }) => collection.id === id
      );
    const collectionNotFoundError = {
      response: {
        data: {
          message: t("Collection not found"),
        },
      },
    };
    if (!updatedProductCollection) throw collectionNotFoundError;
    const rowIdx = collection.value_rows!.findIndex(
      ({ row_id }) => row_id === rowId
    );
    const rowNotFoundError = {
      response: {
        data: {
          message: t("Could not find row id"),
        },
      },
    };
    if (rowIdx === -1) throw rowNotFoundError;
    return updatedProductCollection.value_rows![rowIdx].row_id;
  };

  const isEmpty = (
    value: FormValue[string] | AttributeValue["value"]
  ): boolean => {
    if (
      value === undefined ||
      value === null ||
      value === "" ||
      value === " "
    ) {
      return true;
    }

    if (Array.isArray(value)) {
      return value.length === 0;
    }

    if (typeof value === "object") {
      if (!value) {
        return true;
      }

      // Handle different object types
      if ("value" in value) {
        return isEmpty((value as OptionType<string>).value);
      }
      if ("id" in value) {
        return isEmpty((value as IproductReferenceCollectionItem).id);
      }
      if ("asset_id" in value) {
        return isEmpty((value as ImageAttributeValue).asset_id);
      }
      if ("url" in value) {
        return isEmpty((value as { url: string; display_text: string }).url);
      }

      return true;
    }

    return false;
  };

  // Combined function for both add and edit row form processing
  const processCollectionFormValues = (
    formValues: Record<string, any>,
    attrMap: Map<string, AttributeSchema>,
    targetRow?: { values: AttributeValue[] }
  ) => {
    const values: {
      attribute_id: string;
      value: string | { display_text: string; url: string };
    }[] = [];
    const values_to_delete: string[] = [];

    if (targetRow) {
      // Edit mode: handle add, update, and delete
      const attrToValueMap = targetRow.values.reduce((acc, value) => {
        const existing = acc[value.attribute_id];
        acc[value.attribute_id] = existing
          ? [...(Array.isArray(existing) ? existing : [existing]), value]
          : value;
        return acc;
      }, {} as Record<string, AttributeValue | AttributeValue[]>);

      Object.entries(formValues).forEach(([attribute_id, value]) => {
        const attrValue = attrToValueMap[attribute_id] as
          | AttributeValue
          | AttributeValue[]
          | undefined;
        const attr = attrMap.get(attribute_id);
        if (!attr) return;

        // Handle empty values - mark existing values for deletion
        if (isEmpty(value)) {
          if (
            attrValue &&
            (Array.isArray(attrValue)
              ? attrValue.length > 0
              : !isEmpty(attrValue.value))
          ) {
            values_to_delete.push(
              ...(Array.isArray(attrValue)
                ? attrValue.map((v) => v.id)
                : [attrValue.id])
            );
          }
          return;
        }

        // Handle array values
        if (Array.isArray(value)) {
          const attrValueArray = Array.isArray(attrValue)
            ? attrValue
            : attrValue
            ? [attrValue]
            : [];

          // Delete values that no longer exist in new array
          const valuesToDelete = attrValueArray
            .filter(
              (existingVal) =>
                !value.some((newVal) =>
                  isExistingAttributeFormValue(
                    (newVal as ChipType)?.name ||
                      (newVal as ChipType)?.id ||
                      (newVal as string),
                    existingVal.value,
                    attr.input_type
                  )
                )
            )
            .map((v) => v.id);
          values_to_delete.push(...valuesToDelete);

          // Add new values that don't exist yet
          const valuesToAdd = (value as Array<ChipType | string>)
            .filter((newVal) => {
              const updated_value =
                (newVal as ChipType)?.name ||
                (newVal as ChipType)?.id ||
                (newVal as string);
              return !attrValueArray.some((existingVal) =>
                isExistingAttributeFormValue(
                  updated_value,
                  existingVal.value,
                  attr.input_type
                )
              );
            })
            .map((v) => ({
              attribute_id,
              value:
                (v as ChipType)?.name ||
                ((v as ChipType)?.id as string) ||
                (v as string),
            }));
          values.push(...valuesToAdd);
          return;
        }

        // Handle single values
        if (!Array.isArray(attrValue)) {
          const updated_value =
            value !== undefined && value !== null
              ? (value as OptionType<string>)?.value ?? value
              : null;
          const updated_date_value =
            attr.input_type === "date"
              ? moment(updated_value).format("MMM DD, YYYY")
              : updated_value;

          if (
            !attrValue ||
            typeof updated_date_value === "boolean" ||
            (updated_date_value &&
              !isExistingAttributeFormValue(
                updated_date_value,
                attrValue.value,
                attr.input_type
              ))
          ) {
            values.push({ attribute_id, value: updated_value! });
          }
        } else {
          values.push({ attribute_id, value: value.value });
        }
      });
    } else {
      // Add mode: only add non-empty values
      Object.entries(formValues).forEach(([attribute_id, value]) => {
        const attr = attrMap.get(attribute_id);
        if (!attr) return;
        if (Array.isArray(value)) {
          values.push(
            ...(value as Array<ChipType | string>).map((v) => ({
              attribute_id,
              value:
                ((v as ChipType)?.id as string) ||
                (v as ChipType)?.name ||
                (v as string),
            }))
          );
        } else {
          const updated_value = value
            ? (value as OptionType<string>)?.value || (value as string)
            : null;
          if (!isEmpty(updated_value)) {
            values.push({
              attribute_id,
              value: updated_value!,
            });
          }
        }
      });
    }
    return { values, values_to_delete };
  };

  // Update onEditRow to use the new function
  const onEditRow = async (targetRowValues: OnSubmitValue) => {
    const { row_id, ...formValue } = targetRowValues;
    collection.columns.forEach(({ id: attribute_id, is_not_applicable }) => {
      if (is_not_applicable) {
        delete formValue[attribute_id];
      }
    });
    const targetRow = collection.value_rows?.find(
      ({ row_id: cur_row_id }) => row_id === cur_row_id
    );

    const { values, values_to_delete } = processCollectionFormValues(
      formValue,
      attrMap,
      targetRow
    );
    try {
      if (!values.length && !values_to_delete.length) {
        return t("No changes to save");
      }
      const { data: updatedProduct } = await updateProductStatus();
      const { data } = await editCollectionRow({
        row_id: getUpdatedProductRowId(row_id, updatedProduct),
        values,
        values_to_delete,
        tenant_id,
        product_id: updatedProduct.id,
        collection_id: collection.id,
      });
      if (
        data.display_status === "staged" ||
        data.display_status === "unpublished_staged"
      ) {
        const product_id =
          data.product_number ?? data.primary_staged_product_id ?? data.id;
        replaceProductId(product_id);
      } else {
        fetchProductData();
      }
    } catch (error) {
      throw error;
    }
  };

  // Update onAddRow to use the new function
  const onAddRow = async (rowFormValues: OnSubmitValue) => {
    const { row_id, ...formValues } = rowFormValues;
    collection.columns.forEach(({ id, is_not_applicable }) => {
      if (is_not_applicable) {
        delete rowFormValues[id];
      }
    });

    const { values } = processCollectionFormValues(formValues, attrMap);
    if (!values.length) {
      return t("No changes to save");
    }
    try {
      const { data: updatedProduct } = await updateProductStatus();
      const { data } = await addRow({
        values,
        tenant_id,
        product_id: updatedProduct.id,
        collection_id: collection.id,
      });
      if (
        data.display_status === "staged" ||
        data.display_status === "unpublished_staged"
      ) {
        const product_id =
          data.product_number ?? data.primary_staged_product_id ?? data.id;
        replaceProductId(product_id);
      } else {
        fetchProductData();
      }
    } catch (error) {
      throw error;
    }
  };

  const onDeleteRow = async (row_id: string) => {
    try {
      const { data: updatedProduct } = await updateProductStatus();
      await deleteRow({
        tenant_id,
        collection_id: collection.id,
        product_id: updatedProduct.id,
        row_id: getUpdatedProductRowId(row_id, updatedProduct),
      });
      if (
        updatedProduct.display_status === "staged" ||
        updatedProduct.display_status === "unpublished_staged"
      ) {
        const product_id =
          updatedProduct.product_number ??
          updatedProduct.primary_staged_product_id ??
          updatedProduct.id;
        replaceProductId(product_id);
      } else {
        await fetchProductData();
      }
    } catch (error) {
      throw error;
    }
  };

  const onRowSubmit = async (values: OnSubmitValue) => {
    if (values.row_id.includes(GENERATED_UUID)) {
      return onAddRow(values);
    } else {
      return onEditRow(values);
    }
  };

  const isSKUCollection = collection.object_type === "skus";
  const canShowSKUConfig = isSKUCollection && !isPortfolio;

  return (
    <div className="product-collection-card">
      <ProductCollectionSchemaTable
        collectionSchema={collection}
        title={toTitleCase(
          collection.display_name
            ? t([collection.display_name])
            : t(collection.name)
        )}
        onSubmitForm={onRowSubmit}
        onDeleteRow={onDeleteRow}
        showMandatoryAsterisk={
          product.product_schema.show_mandatory_completion_score && !isPortfolio
        }
        fetchProductData={fetchProductData}
        isPortfolio={isPortfolio}
        productID={product.id}
        productStatus={product.status}
        isEditable={product.is_editable}
        pimProductType={product.pim_product_type}
      />
      {canShowSKUConfig && (
        <CustomSKUConfig
          allow_custom_sku={product.allow_custom_sku}
          replaceProductId={replaceProductId}
          updateProductStatus={updateProductStatus}
          fetchProductData={fetchProductData}
          productIsEditable={
            product.is_editable && product.status !== "archived"
          }
        />
      )}
    </div>
  );
};
